/**
 * Main App Component
 */
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import ClientDashboard from "./pages/ClientDashboard";
import ClientDetailPage from "./pages/ClientDetailPage";
import ExerciseLibraryPage from "./pages/ExerciseLibraryPage";
import WorkoutTemplatesPage from "./pages/WorkoutTemplatesPage";
import WorkoutBuilderPage from "./pages/WorkoutBuilderPage";
import WorkoutPlayerPage from "./pages/WorkoutPlayerPage";
import ProgressPage from "./pages/ProgressPage";
import NutritionPage from "./pages/NutritionPage";
import HabitsPage from "./pages/HabitsPage";
import CheckinPage from "./pages/CheckinPage";
import MessagesPage, { MessagesLockedPage } from "./pages/MessagesPage";
import { Loader2 } from "lucide-react";

// Protected route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Home redirect based on auth
function HomeRedirect() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/clients/:clientId" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ClientDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/exercises" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ExerciseLibraryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/templates" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <WorkoutTemplatesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/templates/new" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <WorkoutBuilderPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/templates/:templateId" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <WorkoutBuilderPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <ClientDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workout/:workoutId" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <WorkoutPlayerPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/progress" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <ProgressPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/nutrition" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <NutritionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/habits" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <HabitsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/checkin" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <CheckinPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute allowedRoles={["admin", "client"]}>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages/:partnerId" 
        element={
          <ProtectedRoute allowedRoles={["admin", "client"]}>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages-locked" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <MessagesLockedPage />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
